import { useEffect, useState, useRef } from "react";
import { Upload, Save, Eye, EyeOff, Copy, MessageCircle, Mail, CreditCard, Shield, Globe, ShieldCheck, Bell, Send, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { getToken } from "../../lib/auth";

const SECTIONS = [
  { id: "branding", label: "Branding", icon: Globe },
  { id: "contacts", label: "Contacts", icon: MessageCircle },
  { id: "payment", label: "Payment", icon: CreditCard },
  { id: "captcha", label: "Captcha (Turnstile)", icon: ShieldCheck },
  { id: "email", label: "Email (SMTP)", icon: Mail },
  { id: "notifications", label: "Admin notifications", icon: Bell },
  { id: "security", label: "Security", icon: Shield },
];

const initial = {
  site_name: "", announcement_text: "", announcement_active: true, logo_url: "",
  support_email: "", whatsapp_number: "", imessage_number: "", contact_widget_active: true,
  stripe_secret_key: "", stripe_publishable_key: "", stripe_webhook_secret: "",
  turnstile_site_key: "", turnstile_secret_key: "", turnstile_active: false,
  smtp_host: "", smtp_port: 587, smtp_user: "", smtp_password: "",
  smtp_from_name: "", smtp_from_email: "",
  admin_access_code: "", admin_email: "", admin_password: "",
  admin_notification_email: "", admin_notification_enabled: true,
  telegram_bot_token: "", telegram_chat_id: "", telegram_notification_enabled: true,
};

export default function AdminSettings() {
  const [s, setS] = useState(initial);
  const [section, setSection] = useState("branding");
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS((prev) => ({ ...prev, ...r.data })));
  }, []);

  const upd = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/settings", s);
      setS((prev) => ({ ...prev, ...r.data }));
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    const res = await api.post("/admin/upload", fd, {
      headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${getToken()}` },
    });
    upd("logo_url", `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
    toast.success("Logo uploaded");
  };

  return (
    <div data-testid="admin-settings-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#00FF66]">// settings</div>
          <h1 className="font-heading font-bold text-3xl mt-1">Site & integrations</h1>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-settings-btn" className="btn-primary">
          <Save className="w-4 h-4" /> {saving ? "saving..." : "save changes"}
        </button>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        <aside className="space-y-1">
          {SECTIONS.map((sec) => (
            <button
              key={sec.id}
              onClick={() => setSection(sec.id)}
              data-testid={`settings-section-${sec.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                section === sec.id ? "bg-[#00FF66]/10 text-[#00FF66] border border-[#00FF66]/20" : "text-[#A1A1AA] hover:bg-white/5 border border-transparent"
              }`}
            >
              <sec.icon className="w-4 h-4" /> {sec.label}
            </button>
          ))}
        </aside>

        <div className="space-y-6">
          {section === "branding" && (
            <Section title="Branding & banner">
              <Field label="site name">
                <input data-testid="site-name-input" value={s.site_name || ""} onChange={(e) => upd("site_name", e.target.value)} className="input-term" />
              </Field>
              <Field label="announcement text (top banner)">
                <input value={s.announcement_text || ""} onChange={(e) => upd("announcement_text", e.target.value)} className="input-term" />
                <Toggle label="display announcement banner" checked={!!s.announcement_active} onChange={(v) => upd("announcement_active", v)} />
              </Field>
              <Field label="logo">
                <div className="flex items-center gap-4">
                  {s.logo_url && <img src={s.logo_url} alt="" className="w-16 h-16 surface object-cover" />}
                  <button onClick={() => fileRef.current?.click()} className="btn-ghost"><Upload className="w-3.5 h-3.5" />upload logo</button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
                  {s.logo_url && <button onClick={() => upd("logo_url", "")} className="btn-ghost hover:!border-[#FF3B30] hover:!text-[#FF3B30]">remove</button>}
                </div>
              </Field>
            </Section>
          )}

          {section === "contacts" && (
            <Section title="Support & contact widget">
              <Field label="support email">
                <input value={s.support_email || ""} onChange={(e) => upd("support_email", e.target.value)} className="input-term" placeholder="support@yourdomain.com" />
              </Field>
              <Field label="whatsapp number (international format with +)">
                <input data-testid="whatsapp-input" value={s.whatsapp_number || ""} onChange={(e) => upd("whatsapp_number", e.target.value)} className="input-term" placeholder="+62818404999" />
              </Field>
              <Field label="imessage number / sms (international format with +)">
                <input data-testid="imessage-input" value={s.imessage_number || ""} onChange={(e) => upd("imessage_number", e.target.value)} className="input-term" placeholder="+62818404999" />
              </Field>
              <Toggle label="show floating contact widget on public pages" checked={!!s.contact_widget_active} onChange={(v) => upd("contact_widget_active", v)} testid="contact-widget-toggle" />
            </Section>
          )}

          {section === "payment" && (
            <Section title="Stripe payment">
              <SecretField label="stripe secret key" value={s.stripe_secret_key} onChange={(v) => upd("stripe_secret_key", v)} testid="stripe-secret" hint="starts with sk_test_ or sk_live_" />
              <SecretField label="stripe publishable key" value={s.stripe_publishable_key} onChange={(v) => upd("stripe_publishable_key", v)} testid="stripe-publishable" hint="starts with pk_test_ or pk_live_" />
              <SecretField label="stripe webhook secret" value={s.stripe_webhook_secret} onChange={(v) => upd("stripe_webhook_secret", v)} testid="stripe-webhook" hint="whsec_..." />
              <p className="font-mono text-[11px] text-[#71717A]">leave blank to keep existing key. dotted preview shows masked stored value.</p>
            </Section>
          )}

          {section === "captcha" && (
            <Section title="Cloudflare Turnstile (Captcha)">
              <Toggle label="enable captcha on checkout" checked={!!s.turnstile_active} onChange={(v) => upd("turnstile_active", v)} testid="turnstile-active" />
              <Field label="site key (public)">
                <input
                  data-testid="turnstile-sitekey"
                  value={s.turnstile_site_key || ""}
                  onChange={(e) => upd("turnstile_site_key", e.target.value)}
                  className="input-term"
                  placeholder="0x4AAAAAAA..."
                />
                <p className="font-mono text-[10px] text-[#71717A] mt-1">visible in HTML; safe to expose.</p>
              </Field>
              <SecretField label="secret key (server-side)" value={s.turnstile_secret_key} onChange={(v) => upd("turnstile_secret_key", v)} testid="turnstile-secret" hint="never exposed to browser. used to verify tokens server-side." />
              <p className="font-mono text-[11px] text-[#71717A]">
                get keys from <code className="text-[#00FF66]">dash.cloudflare.com → Turnstile</code>
              </p>
            </Section>
          )}

          {section === "email" && (
            <Section title="SMTP / email delivery">
              <div className="grid grid-cols-2 gap-3">
                <Field label="smtp host"><input value={s.smtp_host || ""} onChange={(e) => upd("smtp_host", e.target.value)} className="input-term" placeholder="smtp.office365.com" /></Field>
                <Field label="smtp port"><input type="number" value={s.smtp_port || 587} onChange={(e) => upd("smtp_port", parseInt(e.target.value) || 587)} className="input-term" /></Field>
                <Field label="smtp user (login)"><input value={s.smtp_user || ""} onChange={(e) => upd("smtp_user", e.target.value)} className="input-term" placeholder="order@yourdomain.com" /></Field>
                <SecretField label="smtp password" value={s.smtp_password} onChange={(v) => upd("smtp_password", v)} testid="smtp-password" />
                <Field label="from name"><input value={s.smtp_from_name || ""} onChange={(e) => upd("smtp_from_name", e.target.value)} className="input-term" placeholder="Genuine Software" /></Field>
                <Field label="from email"><input value={s.smtp_from_email || ""} onChange={(e) => upd("smtp_from_email", e.target.value)} className="input-term" placeholder="order@yourdomain.com" /></Field>
              </div>
            </Section>
          )}

          {section === "notifications" && (
            <NotificationsSection s={s} upd={upd} />
          )}

          {section === "security" && (
            <Section title="Admin access">
              <Field label="admin login email">
                <input
                  data-testid="admin-email-input"
                  type="email"
                  value={s.admin_email || ""}
                  onChange={(e) => upd("admin_email", e.target.value)}
                  className="input-term"
                  placeholder="admin@yourdomain.com"
                />
                <p className="font-mono text-[10px] text-[#71717A] mt-1">changing this will update your sign-in email on next login.</p>
              </Field>
              <Field label="admin login password">
                <SecretInput value={s.admin_password} onChange={(v) => upd("admin_password", v)} testid="admin-password-input" />
                <p className="font-mono text-[10px] text-[#71717A] mt-1">leave blank to keep current password.</p>
              </Field>
              <Field label="admin url access code">
                <SecretInput value={s.admin_access_code} onChange={(v) => upd("admin_access_code", v)} testid="admin-code" />
                <p className="font-mono text-[11px] text-[#71717A] mt-2">
                  admin login URL: <code className="text-[#00FF66]">{`${window.location.origin}/admin/login?<code>`}</code> — without the code, the page redirects to home.
                </p>
              </Field>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="surface p-6">
      <h2 className="font-heading font-semibold text-lg mb-5">{title}</h2>
      <div className="space-y-5">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-xs uppercase tracking-widest text-[#A1A1AA] block mb-2">{label}</span>
      {children}
    </label>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 mt-2 font-mono text-xs cursor-pointer">
      <input data-testid={testid} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-[#00FF66]" />
      {label}
    </label>
  );
}

function SecretField({ label, value, onChange, testid, hint }) {
  return (
    <Field label={label}>
      <SecretInput value={value} onChange={onChange} testid={testid} />
      {hint && <p className="font-mono text-[10px] text-[#71717A] mt-1">{hint}</p>}
    </Field>
  );
}

function SecretInput({ value, onChange, testid }) {
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(false);
  const masked = value || "";
  const isMasked = masked.includes("•");

  const copy = () => {
    if (!masked) return;
    navigator.clipboard.writeText(masked);
    toast.success("Copied (masked) to clipboard");
  };

  return (
    <div className="relative">
      <input
        data-testid={testid}
        type={show ? "text" : "password"}
        value={editing ? value : (isMasked ? masked : value || "")}
        onFocus={() => { if (isMasked) { setEditing(true); onChange(""); } }}
        onChange={(e) => onChange(e.target.value)}
        placeholder={isMasked ? "•••• stored — type to replace ••••" : "enter value"}
        className="input-term pr-20"
        autoComplete="new-password"
      />
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
        <button type="button" onClick={() => setShow((v) => !v)} className="p-1.5 text-[#71717A] hover:text-[#00FF66]" tabIndex={-1}>
          {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </button>
        {isMasked && (
          <button type="button" onClick={copy} className="p-1.5 text-[#71717A] hover:text-[#00FF66]" tabIndex={-1}>
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}


function NotificationsSection({ s, upd }) {
  const [chats, setChats] = useState([]);
  const [detecting, setDetecting] = useState(false);
  const [testing, setTesting] = useState(false);

  const detectChatId = async () => {
    setDetecting(true);
    try {
      const r = await api.get("/admin/notifications/telegram/updates");
      const list = r.data?.chats || [];
      setChats(list);
      if (list.length === 0) {
        toast.info("No chats found yet — message your bot once on Telegram, then click Detect again.");
      } else {
        toast.success(`Found ${list.length} chat${list.length > 1 ? "s" : ""}. Click one to use its chat_id.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fetch updates. Save bot token first.");
    } finally {
      setDetecting(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/notifications/test", {});
      const { email, telegram, errors } = r.data || {};
      if (email && telegram) toast.success("Test sent: email ✓ telegram ✓");
      else if (email) toast.success("Email ✓ — Telegram failed: " + (errors || []).join("; "));
      else if (telegram) toast.success("Telegram ✓ — Email failed: " + (errors || []).join("; "));
      else toast.error("Both failed: " + (errors || []).join("; "));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <Section title="Admin notifications (after each paid order)">
      <p className="font-mono text-[11px] text-[#71717A] -mt-2">
        Sent to you in addition to the customer&apos;s delivery email — full order details, IP, items, totals, and delivered keys.
      </p>

      <div className="border-t border-white/5 pt-5">
        <h3 className="font-heading font-semibold text-sm mb-4 flex items-center gap-2"><Mail className="w-4 h-4 text-[#00FF66]" /> Email notification</h3>
        <Toggle label="enable email notifications to admin" checked={!!s.admin_notification_enabled} onChange={(v) => upd("admin_notification_enabled", v)} testid="admin-email-notif-toggle" />
        <Field label="admin notification email">
          <input
            data-testid="admin-notification-email-input"
            type="email"
            value={s.admin_notification_email || ""}
            onChange={(e) => upd("admin_notification_email", e.target.value)}
            className="input-term"
            placeholder="kevinfnandez@gmail.com"
          />
          <p className="font-mono text-[10px] text-[#71717A] mt-1">uses the SMTP config from the Email section.</p>
        </Field>
      </div>

      <div className="border-t border-white/5 pt-5">
        <h3 className="font-heading font-semibold text-sm mb-4 flex items-center gap-2"><Send className="w-4 h-4 text-[#00FF66]" /> Telegram notification</h3>
        <Toggle label="enable telegram notifications to admin" checked={!!s.telegram_notification_enabled} onChange={(v) => upd("telegram_notification_enabled", v)} testid="admin-telegram-notif-toggle" />
        <SecretField label="telegram bot token" value={s.telegram_bot_token} onChange={(v) => upd("telegram_bot_token", v)} testid="telegram-bot-token" hint="get from @BotFather (format: 12345:AAA...)." />

        <Field label="telegram chat id">
          <input
            data-testid="telegram-chat-id-input"
            value={s.telegram_chat_id || ""}
            onChange={(e) => upd("telegram_chat_id", e.target.value)}
            className="input-term"
            placeholder="5744525629"
          />
          <p className="font-mono text-[10px] text-[#71717A] mt-1">numeric id of your private chat / group / channel where the bot will post.</p>
        </Field>

        <div className="flex flex-wrap gap-2 mt-2">
          <button
            type="button"
            onClick={detectChatId}
            disabled={detecting}
            data-testid="detect-chat-id-btn"
            className="btn-ghost"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${detecting ? "animate-spin" : ""}`} />
            {detecting ? "detecting..." : "detect chat id"}
          </button>
          <button
            type="button"
            onClick={sendTest}
            disabled={testing}
            data-testid="send-test-notification-btn"
            className="btn-ghost"
          >
            <Send className="w-3.5 h-3.5" />
            {testing ? "sending..." : "send test notification"}
          </button>
        </div>

        {chats.length > 0 && (
          <div data-testid="telegram-chats-list" className="mt-3 surface p-3 space-y-1.5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#71717A]">detected chats — click to use</div>
            {chats.map((c) => (
              <button
                key={c.chat_id}
                type="button"
                onClick={() => { upd("telegram_chat_id", c.chat_id); toast.success(`chat_id set to ${c.chat_id}`); }}
                className="w-full text-left flex items-center justify-between px-3 py-2 rounded-md hover:bg-white/5 font-mono text-xs"
              >
                <span className="text-[#00FF66]">{c.chat_id}</span>
                <span className="text-[#A1A1AA]">{c.type} · {c.title || c.username || "—"}</span>
              </button>
            ))}
          </div>
        )}

        <p className="font-mono text-[11px] text-[#71717A] mt-3 leading-relaxed">
          <span className="text-[#00FF66]">setup:</span> 1. create bot via <code className="text-[#00FF66]">@BotFather</code> → paste token here → save.
          2. open Telegram → message your bot once (any text). 3. click <em>detect chat id</em> → pick your chat. 4. click <em>send test notification</em> to confirm.
        </p>
      </div>
    </Section>
  );
}
